import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  FieldLabel,
  GoogleMark,
  Headline,
  OrDivider,
  PageEyebrow,
  SplitShell,
  TermsLine,
} from '../components/auth-shells.js';
import { PasswordInput } from '../components/password-input.js';
import { PasswordStrength } from '../components/password-strength.js';
import { magToast } from '../components/toaster.js';
import { signIn, signUp } from '../lib/auth-client.js';
import { founderErrorCopy } from '../lib/error-copy.js';

// ?next=<encoded-relative-path> — supplied by the invite accept flow so
// the new account lands back on /accept?token=… after the auth round-trip.
// Same-origin only.
// ?email=<address> — pre-fills the email input. Used by the accept flow
// so the invitee doesn't retype the address that was already on the
// invite. Read-only when present so it can't be edited to bypass the
// email-match check on accept.
const searchSchema = z.object({
  next: z.string().optional(),
  email: z.string().optional(),
});

function safeNext(next: string | undefined): string | null {
  if (!next) return null;
  try {
    const decoded = decodeURIComponent(next);
    if (!decoded.startsWith('/')) return null;
    return decoded;
  } catch {
    return null;
  }
}

export const Route = createFileRoute('/signup')({
  validateSearch: searchSchema,
  component: SignupPage,
});

// Handoff §15.2.2 — split shell. Name + work email + password + Google.
// Casting Mag quote in the brand panel.
function SignupPage() {
  const { t } = useLingui();

  const router = useRouter();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const nextPath = safeNext(search.next);
  // First-touch destination after signup:
  //   - invite leg → nextPath (the /accept token URL)
  //   - normal signup → /onboarding/welcome (12.2L-51) where mag
  //     introduces herself + the founder names the workspace. that
  //     route navigates onward to /brands (create first brand) once
  //     submitted — payment moved to the end of brand onboarding.
  const landing = nextPath ?? '/onboarding/welcome';
  const callbackURL = `${window.location.origin}${landing}`;

  // Pre-fill from ?email= when present (invite flow). The field stays
  // editable so a typo on the invite is still recoverable, but the
  // accept endpoint will refuse if the session email doesn't match
  // — that's the real defense.
  const prefilledEmail = search.email?.trim() ?? '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<'google' | 'password' | null>(null);
  const [errorBanner, setErrorBanner] = useState<{ title: string; hint?: string } | null>(null);

  const clearError = () => setErrorBanner(null);

  const handleGoogle = async () => {
    clearError();
    setPending('google');
    let result: Awaited<ReturnType<typeof signIn.social>>;
    try {
      result = await signIn.social({
        provider: 'google',
        callbackURL,
      });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      setErrorBanner({ title: "google didn't open the door.", hint: message });
      magToast.error(i18n._(msg`google didn't open the door.`), message);
      setPending(null);
      return;
    }
    if (result.error) {
      const message = result.error.message ?? 'try again.';
      setErrorBanner({ title: "google didn't open the door.", hint: message });
      magToast.error(i18n._(msg`google didn't open the door.`), message);
      setPending(null);
      return;
    }
    // Same fix as login.tsx — better-auth returns the oauth url in
    // data.url; the dashboard has to navigate to it. without this the
    // user lands on the dashboard 401'd since they never went through
    // google.
    const redirectUrl = (result.data as { url?: string; redirect?: boolean } | undefined)?.url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    await router.invalidate();
    window.location.href = landing;
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPending('password');
    let result: Awaited<ReturnType<typeof signUp.email>>;
    try {
      result = await signUp.email({
        email,
        password,
        name,
        callbackURL,
      });
    } catch (err) {
      const message = founderErrorCopy(err, 'try again.');
      setErrorBanner({ title: "couldn't create the account.", hint: message });
      magToast.error(i18n._(msg`couldn't create the account.`), message);
      setPending(null);
      return;
    }
    if (result.error) {
      const message = result.error.message ?? 'an account may already exist for that email.';
      setErrorBanner({ title: "couldn't create the account.", hint: message });
      magToast.error(i18n._(msg`couldn't create the account.`), message);
      setPending(null);
      return;
    }
    // No conversion is reported here, deliberately. `account_created` fires
    // from AnalyticsIdentityMount for BOTH signup paths, off a server-side
    // claim, because the Google leg never returns to this component — the
    // browser leaves for Google and comes back as a fresh document load.
    //
    // This used to fire inline and the comment claimed the Google case was
    // covered by AnalyticsIdentityMount's identify. It was not: identify is
    // suppressed on the dashboard by data-exclude-paths="/*", and `identify`
    // is not a conversion name in any case, so it could never have produced
    // this conversion. Google signups reported nothing at all (task 5f7a2f1).
    await router.invalidate();
    if (nextPath) {
      window.location.href = nextPath;
    } else {
      navigate({ to: '/onboarding/welcome' });
    }
  };

  return (
    <SplitShell quote={t`hi. i'm mag. show me your product and we'll find your people.`}>
      <div className="mag-stagger-in" style={{ animationDelay: '320ms' }}>
        <PageEyebrow>
          <Trans>sign up</Trans>
        </PageEyebrow>
      </div>
      <div
        className="mag-stagger-in mt-3 flex flex-col gap-1.5"
        style={{ animationDelay: '400ms' }}
      >
        <Headline>
          <Trans>let's get you in.</Trans>
        </Headline>
        <p className="text-[15px] text-[var(--ink)]/70">
          <Trans>
            already have one?{' '}
            <Link to="/login" className="link-plum">
              sign in →
            </Link>
          </Trans>
        </p>
      </div>

      <div className="mt-7 flex flex-col gap-4">
        {errorBanner ? (
          <div
            role="alert"
            className="rounded-xl border-[1.5px] border-[var(--pulse)] bg-[var(--pulse)]/8 px-4 py-3 shadow-[3px_3px_0_var(--pulse)]"
          >
            <p className="font-display text-[14px] font-semibold leading-tight text-[var(--ink)]">
              {errorBanner.title}
            </p>
            {errorBanner.hint ? (
              <p className="mt-1 text-[12.5px] leading-snug text-[var(--ink)]/70">
                {errorBanner.hint}
              </p>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={pending !== null}
          className="mag-stagger-in btn btn-ghost w-full disabled:opacity-60"
          style={{ animationDelay: '480ms' }}
        >
          <GoogleMark />
          {pending === 'google' ? <Trans>redirecting…</Trans> : <Trans>continue with Google</Trans>}
        </button>

        <div className="mag-stagger-in" style={{ animationDelay: '560ms' }}>
          <OrDivider />
        </div>

        <form
          onSubmit={handlePassword}
          className="mag-stagger-in flex flex-col gap-3"
          style={{ animationDelay: '640ms' }}
        >
          <label className="field">
            <FieldLabel>
              <Trans>your name</Trans>
            </FieldLabel>
            <input
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t`alex chen`}
            />
          </label>
          <label className="field">
            <FieldLabel>
              <Trans>work email</Trans>
            </FieldLabel>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`you@startup.com`}
            />
          </label>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: <PasswordInput> renders a real <input> — biome can't see through the component boundary. */}
          <label className="field">
            <FieldLabel>
              <Trans>password</Trans>
            </FieldLabel>
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              required
              placeholder={t`at least 8 characters`}
            />
            <PasswordStrength password={password} />
          </label>

          <button
            type="submit"
            disabled={pending !== null}
            className="btn mt-2 w-full disabled:opacity-60"
          >
            {pending === 'password' ? (
              <Trans>creating account…</Trans>
            ) : (
              <Trans>create account</Trans>
            )}
            <span className="btn-arrow">→</span>
          </button>
        </form>

        <div className="mag-stagger-in" style={{ animationDelay: '720ms' }}>
          <TermsLine />
        </div>
      </div>
    </SplitShell>
  );
}
